'use client';

import { Box } from '@mui/material';
import { format, isValid, parseISO } from 'date-fns';
import { formatDate, formatDob } from '@/lib/dateTime';
import { useSocietyDateFormat } from '@/hooks/publicSearch/useSocietyDisplayOptions';

type SocietyFormattedDateProps = {
  value: string | Date | null | undefined;
  /** When true, empty/sentinel DOB renders as em dash (detail/list DOB style). */
  asDob?: boolean;
  /** Wrap in a flex box matching animal grid DOB cell layout. */
  gridCell?: boolean;
};

/**
 * Formats a typed date with the current society's DISPLAY_OPTIONS.dateFormat.
 */
export function SocietyFormattedDate({
  value,
  asDob = false,
  gridCell = false,
}: SocietyFormattedDateProps) {
  const dateFormat = useSocietyDateFormat();

  let formatted = '';
  if (asDob) {
    // Match formatDob: unset / sentinel → em dash; empty → em dash.
    if (value instanceof Date) {
      formatted = formatDate(value, dateFormat) || '—';
    } else {
      formatted = formatDob(
        typeof value === 'string' ? value : null,
        dateFormat,
      );
    }
  } else if (value instanceof Date) {
    formatted = formatDate(value, dateFormat);
  } else if (typeof value === 'string' && value) {
    const parsed = parseISO(value);
    formatted = isValid(parsed)
      ? format(parsed, dateFormat)
      : formatDate(new Date(value), dateFormat) || value;
  }

  if (!formatted) return null;

  if (gridCell) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        {formatted}
      </Box>
    );
  }

  return <>{formatted}</>;
}
